import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import LanguageToggle from "./language-toggle/LanguageToggle";
import EnglishRevertModal from "./english-revert-modal/EnglishRevertModal";
import SpanishAcknowledgmentModal from "components/v2/spanish-acknowledgment-modal/SpanishAcknowledgmentModal";
import { DEFAULT_LANGUAGE, LANG_PREF_COOKIE_NAME, LOCAL_STORAGE_KEY, SPANISH_LANG_CODE, SUPPORTED_LANGUAGES } from "utilities/Constant";
import { useGetFeatureFlagsQuery } from "services/common/featureFlagService";
import { selectHasLoans } from "features/preferredLanguageSlice";
import { getCurrentUserId, getDealApplicationId, getDealBorrowerId, getHasAcknowledgedSpanishPrompt, getHasPreferredLanguageOverride, selectPreferredLanguage, setHasAcknowledgedSpanishPrompt, setHasPreferredLanguageOverride, setPreferredLanguage } from "features/authSlice";
import { extractDealServicePreferredLanguage, getPreferredLanguage, toUserDbPreferredLanguage } from "utilities/getPreferredLanguage";
import { useGetBorrowerDetailsQuery, useGetBorrowerPersonalInformationQuery, useUpdateUserPreferredLanguageMutation } from "pages/dashboard/home/dashboardService";

interface PreferredLanguageProps {
  readonly isMobile?: boolean;
  readonly isAuthenticated?: boolean;
}

const setLangPrefCookie = (langCode: string): void => {
  document.cookie = `${LANG_PREF_COOKIE_NAME}=${langCode};path=/`;
};

const clearLangPrefCookie = (): void => {
  document.cookie = `${LANG_PREF_COOKIE_NAME}=;path=/;expires=Thu, 01 Jan 1970 00:00:00 GMT`;
};

const setLocalStorage = (key: string, value: string): void => {
  window.localStorage.setItem(key, value);
};

const getLocalStorage = (key: string): string | null => {
  return window.localStorage.getItem(key);
};

const refreshPageForTranslation = (): void => {
  window.location.reload();
};

const shouldReloadForLanguageChange = (nextLanguage: string): boolean => {
  const currentLanguage = getLocalStorage(LOCAL_STORAGE_KEY);
  return currentLanguage !== nextLanguage;
};

const PreferredLanguage: React.FC<PreferredLanguageProps> = ({ isMobile = false, isAuthenticated = false }) => {
  const dispatch = useDispatch();
  const { data: featureFlag } = useGetFeatureFlagsQuery();
  const reduxLanguage = useSelector(selectPreferredLanguage);
  const userId = useSelector(getCurrentUserId);
  const dealApplicationId = useSelector(getDealApplicationId);
  const dealBorrowerId = useSelector(getDealBorrowerId);
  const hasAcknowledgedSpanishPrompt = useSelector(getHasAcknowledgedSpanishPrompt);
  const hasPreferredLanguageOverride = useSelector(getHasPreferredLanguageOverride);
  const hasLoans = useSelector(selectHasLoans);
  const hasApplicationContext = Boolean(dealApplicationId);
  const [updateUserPreferredLanguage] = useUpdateUserPreferredLanguageMutation();
  const { data: userProfile, isSuccess: isUserProfileSuccess } = useGetBorrowerDetailsQuery(userId, {
    skip:  !userId,
  });
  const { data: dealServicePersonalInformation } = useGetBorrowerPersonalInformationQuery(
    { borrowerId: dealBorrowerId || "" },
    { skip: !isAuthenticated || !hasApplicationContext || !dealBorrowerId },
  );

  const [userLanguage, setUserLanguage] = useState<string>(() => getPreferredLanguage(undefined));
  const [showEnglishRevertModal, setShowEnglishRevertModal] = useState(false);
  const [showAcknowledgmentModal, setShowAcknowledgmentModal] = useState(false);
  const [pendingLanguage, setPendingLanguage] = useState<string | null>(null);
  // Sync toggle when Redux language changes (e.g. after login resolves server value)
  useEffect(() => {
     if (!isAuthenticated) {
       const savedLanguage = getLocalStorage(LOCAL_STORAGE_KEY);
       setUserLanguage(getPreferredLanguage(savedLanguage ?? reduxLanguage));
       return;
     }

    const dealServiceLanguage = extractDealServicePreferredLanguage(dealServicePersonalInformation);
    const resolved = getPreferredLanguage(reduxLanguage?.trim() ? reduxLanguage : dealServiceLanguage);

    const currentUserDbLanguage = userProfile?.preferredLanguage;
    const isUserDbLanguageNull =
      isUserProfileSuccess &&
      (currentUserDbLanguage == null || currentUserDbLanguage.trim() === "");
    const shouldPromptSpanishAcknowledgment =
      isAuthenticated &&
      resolved === "Español" && 
      isUserDbLanguageNull &&
      !hasAcknowledgedSpanishPrompt &&
      !showAcknowledgmentModal; 

    if (!shouldPromptSpanishAcknowledgment) {
      const shouldReload = shouldReloadForLanguageChange(resolved);
      setUserLanguage(resolved);
      setLocalStorage(LOCAL_STORAGE_KEY, resolved);

      if (shouldReload) {
        refreshPageForTranslation();
      }
    }

    if (shouldPromptSpanishAcknowledgment) { 
      setPendingLanguage(resolved);
      setShowAcknowledgmentModal(true);
    }
  }, [
    dealServicePersonalInformation,
    reduxLanguage,
    isAuthenticated,
    isUserProfileSuccess,
    userProfile?.preferredLanguage,
    hasPreferredLanguageOverride,
    hasAcknowledgedSpanishPrompt,
    showAcknowledgmentModal,
  ]);

  
  useEffect(() => {
    const savedLanguage = getLocalStorage(LOCAL_STORAGE_KEY);
    if (savedLanguage === "Español") {
      setLangPrefCookie(SPANISH_LANG_CODE);
    } else {
      clearLangPrefCookie();
    }
  }, []);

  if (!featureFlag?.preferredLanguageEnabled) {
    return null;
  }

  const shouldShowAcknowledgmentModal = true;
  const shouldShowEnglishRevertModal = true;

  const applyLanguageChange = (value: string) => {
    const shouldReload = shouldReloadForLanguageChange(value);
    setUserLanguage(value);
    setLocalStorage(LOCAL_STORAGE_KEY, value);

    if (isAuthenticated) {
      dispatch(setPreferredLanguage(value));
      dispatch(setHasPreferredLanguageOverride(true));
    }
     if (value === "Español") {
      setLangPrefCookie(SPANISH_LANG_CODE);
    } else {
      clearLangPrefCookie();
    }

    if (shouldReload) {
      refreshPageForTranslation();
    }
  };

  const persistLanguageChange = (value: string) => {
    if (!isAuthenticated || !userId) {
      return;
    }

    void updateUserPreferredLanguage({
      userId,
      preferredLanguage: toUserDbPreferredLanguage(value),
    });
  };

  const commitLanguageChange = (value: string) => {
    applyLanguageChange(value);
    persistLanguageChange(value);
  };

  const handleLanguageChange = (value: string) => {
    if (!isAuthenticated) {
      if (value === "Español" && userLanguage !== "Español") {
        setPendingLanguage(value);
        setShowAcknowledgmentModal(true);
        return;
      }

      if (value === DEFAULT_LANGUAGE && userLanguage !== DEFAULT_LANGUAGE) {
        setPendingLanguage(DEFAULT_LANGUAGE);
        setShowEnglishRevertModal(true);
        return;
      }

      applyLanguageChange(value);
      setShowAcknowledgmentModal(false);
      setShowEnglishRevertModal(false);
      return;
    }

    if (value === "Español" && userLanguage !== "Español") {
      setPendingLanguage(value);
      setShowAcknowledgmentModal(true);
      return;
    }

    if (value === DEFAULT_LANGUAGE && userLanguage !== DEFAULT_LANGUAGE) {
       setPendingLanguage(value);
      setShowEnglishRevertModal(true);
      return;
    }

    commitLanguageChange(value);
  };

  const handleAcknowledge = () => {
    const languageToApply = pendingLanguage ?? "Español";
    commitLanguageChange(languageToApply);
    setPendingLanguage(null);
    if (isAuthenticated) {
      dispatch(setHasAcknowledgedSpanishPrompt(true));
    }
    setShowAcknowledgmentModal(false);
    window.location.reload();
  };

  const handleRevertConfirm = () => {
    const languageToApply = pendingLanguage ?? DEFAULT_LANGUAGE;
    commitLanguageChange(languageToApply);
    setPendingLanguage(null);
    setShowEnglishRevertModal(false);
    window.location.reload();
  };

  const handleRevertCancel = () => {
    setPendingLanguage(null);
    setShowEnglishRevertModal(false);
  };

  const isDisabled = isAuthenticated ? !!hasLoans : false;

  return (
    <>
      <div className="preferred-language">
        <LanguageToggle value={userLanguage} onChange={handleLanguageChange} options={SUPPORTED_LANGUAGES} isMobile={isMobile} disabled={isDisabled} />
      </div>
      {shouldShowAcknowledgmentModal && showAcknowledgmentModal && (
        <SpanishAcknowledgmentModal
          onAcknowledge={handleAcknowledge}
          onCancel={() => {
            setShowAcknowledgmentModal(false);
            setPendingLanguage(null);
          }}
        />
      )}
      {shouldShowEnglishRevertModal && showEnglishRevertModal && (
        <EnglishRevertModal onConfirm={handleRevertConfirm} onCancel={handleRevertCancel} />
      )}
    </>
  );
};

export default PreferredLanguage;
